import { useEffect, useRef } from "react";

// Auto-refreshes page data WITHOUT a manual reload. It re-runs `callback`:
//  - when the tab regains focus / visibility (partner ↔ client cross-actions),
//  - on a light interval while the tab is visible (default 20s).
// Uses a ref so the latest callback is always called even if it changes.
export function useAutoRefresh(callback, { intervalMs = 20000, enabled = true } = {}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const run = () => { if (document.visibilityState === "visible") cbRef.current && cbRef.current(); };
    const start = () => { if (!timer) timer = setInterval(run, intervalMs); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { run(); start(); } else { stop(); }
    };
    const onFocus = () => run();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    start();
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs, enabled]);
}

export default useAutoRefresh;
